import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const slugSchema = z.object({ slug: z.string().trim().min(1).max(120) });

export type PublicProduct = {
  id: string;
  slug: string;
  name: string;
  description: string;
  price_cents: number;
  currency: string;
  product_type: string;
  cover_image_url: string | null;
  metadata: {
    status?: string;
    pdf_url?: string;
    preview_url?: string;
    sample_url?: string;
    categories?: string[];
    question_count?: number;
  };
  is_active: boolean;
};


export const getPublicProduct = createServerFn({ method: "GET" })
  .inputValidator((d) => slugSchema.parse(d))
  .handler(async ({ data }): Promise<PublicProduct | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("products")
      .select("id, slug, name, description, price_cents, currency, product_type, cover_image_url, metadata, is_active")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error || !row) return null;
    return row as unknown as PublicProduct;
  });

const claimSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(255),
  slug: z.string().trim().min(1).max(120),
});

export type ClaimResult =
  | { ok: true; download_url: string; duplicate: boolean }
  | { ok: false; reason: string };

/** Early-access claim: collect email, return signed-ish PDF URL.
 *  Records the email on product_waitlist so we can email updates later. */
export const claimEarlyAccess = createServerFn({ method: "POST" })
  .inputValidator((d) => claimSchema.parse(d))
  .handler(async ({ data }): Promise<ClaimResult> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: product, error: pErr } = await supabaseAdmin
      .from("products")
      .select("name, metadata, is_active")
      .eq("slug", data.slug)
      .maybeSingle();
    if (pErr || !product || !(product as any).is_active) {
      return { ok: false, reason: "Product unavailable." };
    }
    const meta = (product as any).metadata ?? {};
    const url = meta.pdf_url as string | undefined;
    if (!url) return { ok: false, reason: "Download not ready yet." };

    // Record email on waitlist (idempotent)
    let duplicate = false;
    try {
      const { data: existing } = await supabaseAdmin
        .from("product_waitlist")
        .select("id")
        .eq("email", data.email)
        .eq("product_name", (product as any).name)
        .maybeSingle();
      if (existing) {
        duplicate = true;
      } else {
        const { error: insErr } = await supabaseAdmin
          .from("product_waitlist")
          .insert({ email: data.email, product_name: (product as any).name });
        if (insErr && (insErr as any).code === "23505") duplicate = true;
      }
    } catch {
      /* never block download on telemetry */
    }

    // A product claim is explicit consent to receive the next pack, so make
    // this lead part of the owned audience instead of trapping it in a
    // product-only waitlist.
    try {
      const { data: existingSubscriber } = await supabaseAdmin
        .from("newsletter_subscribers")
        .select("id")
        .ilike("email", data.email)
        .maybeSingle();
      if (!existingSubscriber) {
        await supabaseAdmin
          .from("newsletter_subscribers")
          .insert({ email: data.email, is_internal: false });
      }
    } catch {
      /* never block download on audience telemetry */
    }

    // Optional confirmation email (best-effort)
    if (!duplicate) {
      try {
        const { enqueueAppEmail } = await import("@/lib/email/enqueue.server");
        await enqueueAppEmail({
          templateName: "product-waitlist",
          recipientEmail: data.email,
          templateData: { productName: (product as any).name },
          idempotencyKey: `early-access-${data.slug}-${data.email}`,
        });
      } catch {
        /* ignore email failures */
      }
    }

    return { ok: true, download_url: url, duplicate };
  });
